import { Injectable } from '@angular/core';
import {
  INPUT_VALUE_STEP_CHARACTER,
  INPUT_VALUE_STEP_MESSAGE,
  INPUT_VALUE_STEP_PHOTO,
  INPUT_VALUE_COVER_TYPE_SOFT,
} from '@shared/common/product';
import { BookService } from '@studiobuki/web-core/lib/book-common';
import type { TCreateUserBookOmitKeys } from '@studiobuki/web-core/lib/firebase';
import { FirebaseService } from 'src/app/services/firebase.service';
import type { TBookMode } from '@studiobuki/web-core/lib/routing';
import { ROUTE_PATH_QUERY_PARAM_MODE_USER } from '@studiobuki/web-core/lib/routing';
import { Logger } from '@shared/common/logger';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import AlphabetTrain from 'src/app/data/books/AlphabetTrain';
import type { IBookFroebelkanAlphabetTrainData } from '@shared/jp/models';
import {
  getKidstitle,
  PHOTO_FROEBELKAN_ALPHABET_TRAIN,
} from '@shared/jp/product';
import { BookPagesAlphabetTrainService } from './book-pages-alphabet-train.service';
// TODO changed to segments from alphabet train

type TBookAlphabetTrainCreateData = Pick<
  IBookFroebelkanAlphabetTrainData,
  | 'heroName'
  | 'kunChan'
  | 'vehicle'
  | 'skinColor'
  | 'hairColor'
  | 'hairStyle'
  | 'glasses'
>;

const log = new Logger('BookAlphabetTrainService');

@Injectable({
  providedIn: 'root',
})
export class BookAlphabetTrainService extends BookService<
  IBookFroebelkanAlphabetTrainData,
  TBookAlphabetTrainCreateData
> {
  constructor(
    protected override _firebaseService: FirebaseService,
    protected override _bookPagesService: BookPagesAlphabetTrainService,
    private _loaderService: LoaderService,
  ) {
    super(_firebaseService, _bookPagesService);
  }

  /** a method to create initial book data */
  // eslint-disable-next-line class-methods-use-this
  protected override _createBookData<
    O extends Partial<IBookFroebelkanAlphabetTrainData>,
  >(
    /** data to create the book from */
    createBookData: TBookAlphabetTrainCreateData,
    /** book data to owerride the defaults */
    overrideBookData: O,
  ) {
    const { kunChan } = createBookData;
    const kidstitle = getKidstitle(kunChan);
    // const heroName = this.bookData?.heroName;
    const newBook: Omit<
      IBookFroebelkanAlphabetTrainData,
      TCreateUserBookOmitKeys
    > = {
      ...createBookData,
      type: 'book',
      steps: {
        [INPUT_VALUE_STEP_CHARACTER]: {
          stepType: INPUT_VALUE_STEP_CHARACTER,
          isDone: true,
        },
        [INPUT_VALUE_STEP_PHOTO]: {
          stepType: INPUT_VALUE_STEP_PHOTO,
          isDone: false,
        },
        [INPUT_VALUE_STEP_MESSAGE]: {
          stepType: INPUT_VALUE_STEP_MESSAGE,
          isDone: false,
        },
      },
      // uid: undefined,
      // bookId: undefined,
      fileUploadStatus: 0,
      alias: AlphabetTrain.alias,
      messageText:
        `${createBookData.heroName}${kidstitle}へ\n` +
        `\n` +
        `${createBookData.heroName}${kidstitle}の\n` +
        `おなまえには\n` +
        `こういうこにそだってほしい\n` +
        `というおもいをこめました。\n` +
        `これからどんどんおおきくなり\n` +
        `いつか、じぶんのおなまえを\n` +
        `すきっていってくれたら\n` +
        `うれしいです。\n` +
        `いつまでもだいすきだよ！`,
      cover: INPUT_VALUE_COVER_TYPE_SOFT,
    };

    return { ...newBook, ...overrideBookData };
  }

  override async loadBook(
    bookId: string,
    onBookDataLoad:
      | ((bookData: IBookFroebelkanAlphabetTrainData) => Promise<void>)
      | ((bookData: IBookFroebelkanAlphabetTrainData) => void),
    mode: TBookMode = ROUTE_PATH_QUERY_PARAM_MODE_USER,
  ) {
    const bookData = await super.loadBook(bookId, onBookDataLoad, mode);

    this._bookPagesService.newBookInit(bookData);

    if (bookData.fileUploadStatus >= 10) {
      await this._thumbFileLoad(PHOTO_FROEBELKAN_ALPHABET_TRAIN);
    }

    this.textChangeSubscribe.next();
    this.messageTextChangeSubscribe.next();

    return bookData;
  }

  override async uploadPhoto(file: Blob) {
    return super.uploadPhoto(file, PHOTO_FROEBELKAN_ALPHABET_TRAIN);
  }
}
